import { z } from 'zod';
import { USER_ROLES } from '../enums';

/**
 * Login form input — validated on both client (form) and server (route handler).
 * Same schema, no drift.
 */
export const loginInputSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(50, 'Username must be at most 50 characters')
    .regex(
      /^[a-zA-Z0-9._-]+$/,
      'Username can only contain letters, numbers, dot, underscore, dash',
    ),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(200, 'Password too long'),
});
export type LoginInput = z.infer<typeof loginInputSchema>;

/**
 * Successful login response — JWT + minimal user context.
 * Full user details should be fetched separately via /me.
 */
export const loginResponseSchema = z.object({
  token: z.string(),
  user: z.object({
    id: z.string().uuid(),
    username: z.string(),
    fullName: z.string(),
    role: z.enum(USER_ROLES),
    employeeId: z.string().uuid().nullable(),
  }),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

/**
 * JWT payload shape — encoded on login, decoded on every protected request.
 * Keep this tiny — Cloudflare Workers have request CPU limits.
 */
export const jwtPayloadSchema = z.object({
  sub: z.string().uuid(), // user id
  username: z.string(),
  role: z.enum(USER_ROLES),
  /** Standard JWT claims — set by jose lib */
  iat: z.number().optional(),
  exp: z.number().optional(),
});
export type JwtPayload = z.infer<typeof jwtPayloadSchema>;
